(function () {
  'use strict';

  var KLAVIYO_REVISION = '2026-07-15';
  var BACK_IN_STOCK_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';

  class ProductBackInStock extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.submitting = false;

      this.section = this.closest('.product-section[data-section-type="product"]');
      this.trigger = this.querySelector('[data-bis-trigger]');
      this.dialog = this.querySelector('[data-bis-dialog]');
      this.form = this.querySelector('[data-bis-form]');
      this.formView = this.querySelector('[data-bis-form-view]');
      this.successView = this.querySelector('[data-bis-success]');
      this.successTitle = this.querySelector('[data-bis-success-title]');
      this.successNote = this.querySelector('[data-bis-success-note]');
      this.emailInput = this.querySelector('[data-bis-email]');
      this.variantInput = this.querySelector('[data-bis-variant]');
      this.status = this.querySelector('[data-bis-status]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.submitLabel = this.querySelector('[data-bis-submit-label]');
      this.submitLoading = this.querySelector('[data-bis-submit-loading]');
      this.marketingInput = this.querySelector('[data-bis-marketing]');
      this.closeButtons = [...this.querySelectorAll('[data-bis-close]')];
      this.dialogLabelledBy = this.dialog && this.dialog.getAttribute('aria-labelledby');
      this.dialogDescribedBy = this.dialog && this.dialog.getAttribute('aria-describedby');

      if (!this.trigger || !this.dialog || !this.form || !this.variantInput || !this.emailInput) return;

      this.handleVariantChange = this.onVariantChange.bind(this);
      this.handleTriggerClick = this.open.bind(this);
      this.handleCloseClick = this.close.bind(this);
      this.handleDialogClose = this.onDialogClose.bind(this);
      this.handleDialogClick = this.onDialogClick.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);

      if (this.section) this.section.addEventListener('variantChange', this.handleVariantChange);
      this.trigger.addEventListener('click', this.handleTriggerClick);
      this.closeButtons.forEach(button => button.addEventListener('click', this.handleCloseClick));
      this.dialog.addEventListener('close', this.handleDialogClose);
      this.dialog.addEventListener('cancel', this.handleDialogClose);
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.form.addEventListener('submit', this.handleSubmit);

      this.variants = this.readProductVariants();
      this.setVariant(this.getInitialVariant());
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      if (this.section) this.section.removeEventListener('variantChange', this.handleVariantChange);
      if (this.trigger) this.trigger.removeEventListener('click', this.handleTriggerClick);
      if (this.closeButtons) {
        this.closeButtons.forEach(button => button.removeEventListener('click', this.handleCloseClick));
      }
      if (this.dialog) {
        this.dialog.removeEventListener('close', this.handleDialogClose);
        this.dialog.removeEventListener('cancel', this.handleDialogClose);
        this.dialog.removeEventListener('click', this.handleDialogClick);
      }
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
      document.documentElement.classList.remove('bis-modal-open');
      this.initialized = false;
    }

    readProductVariants() {
      if (!this.section) return [];
      var variantData = this.section.querySelector('[data-variant-json]');
      if (!variantData) return [];

      try {
        return JSON.parse(variantData.value || variantData.textContent || '[]');
      } catch (error) {
        console.warn('Unable to read product variants for back in stock.', error);
        return [];
      }
    }

    getInitialVariant() {
      var initialId = String(this.dataset.initialVariantId || '');
      var matchedVariant = this.variants.find(variant => String(variant.id) === initialId);
      if (matchedVariant) return matchedVariant;

      return initialId ? {
        id: initialId,
        available: this.dataset.initialVariantAvailable === 'true'
      } : null;
    }

    onVariantChange(event) {
      this.setVariant(event.detail && event.detail.variant);
    }

    setVariant(variant) {
      this.currentVariant = variant || null;
      var isUnavailable = Boolean(variant && !variant.available);
      this.trigger.hidden = !isUnavailable;

      if (isUnavailable && this.variantInput) {
        var option = this.variantInput.querySelector
          ? this.variantInput.querySelector('option[value="' + String(variant.id) + '"]')
          : null;

        if (option || this.variantInput.type === 'hidden') {
          this.variantInput.value = String(variant.id);
        }
      }

      if (!isUnavailable && this.isOpen()) this.close();
    }

    isOpen() {
      return Boolean(this.dialog && (this.dialog.open || this.dialog.hasAttribute('open')));
    }

    open() {
      if (!this.currentVariant || this.currentVariant.available || this.isOpen()) return;
      this.resetView();

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      document.documentElement.classList.add('bis-modal-open');
      window.requestAnimationFrame(() => this.emailInput.focus());
      this.emit('back-in-stock:opened');
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.onDialogClose();
      }
    }

    onDialogClose() {
      document.documentElement.classList.remove('bis-modal-open');
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    resetView() {
      this.formView.hidden = false;
      this.successView.hidden = true;
      if (this.successNote) this.successNote.hidden = true;
      if (this.dialogLabelledBy) {
        this.dialog.setAttribute('aria-labelledby', this.dialogLabelledBy);
      } else {
        this.dialog.removeAttribute('aria-labelledby');
      }
      if (this.dialogDescribedBy) {
        this.dialog.setAttribute('aria-describedby', this.dialogDescribedBy);
      } else {
        this.dialog.removeAttribute('aria-describedby');
      }
      this.setStatus('');
    }

    setLoading(isLoading) {
      this.submitting = isLoading;
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.submitLabel.hidden = isLoading;
      this.submitLoading.hidden = !isLoading;
    }

    setStatus(message, type) {
      this.status.textContent = message;
      this.status.hidden = !message;
      this.status.classList.toggle('is-error', type === 'error');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      this.setStatus('');

      if (!this.emailInput.checkValidity()) {
        this.emailInput.reportValidity();
        this.setStatus('Please enter a valid email address.', 'error');
        return;
      }

      var companyId = (this.dataset.companyId || '').trim();
      var variantId = String(this.variantInput.value || '').trim();
      if (!companyId || !variantId) {
        this.setStatus('Back in stock notifications are not configured yet. Please try again later.', 'error');
        return;
      }

      var wantsMarketing = Boolean(this.marketingInput && this.marketingInput.checked);
      var email = this.emailInput.value.trim();
      this.setLoading(true);
      this.emit('back-in-stock:submitted', { variantId: variantId, marketingRequested: wantsMarketing });

      try {
        await this.createBackInStockSubscription(companyId, variantId, email);

        var marketingSucceeded = null;
        if (wantsMarketing) {
          try {
            await this.createShopifyMarketingSubscription(email);
            marketingSucceeded = true;
          } catch (marketingError) {
            marketingSucceeded = false;
            console.warn('Back in stock alert saved, but Shopify marketing signup failed.', marketingError);
          }
        }

        this.form.reset();
        this.setVariant(this.currentVariant);
        this.formView.hidden = true;
        this.successView.hidden = false;
        if (this.successTitle && this.successTitle.id) {
          this.dialog.setAttribute('aria-labelledby', this.successTitle.id);
          this.dialog.removeAttribute('aria-describedby');
        }
        if (this.successNote) this.successNote.hidden = marketingSucceeded !== false;
        this.emit('back-in-stock:succeeded', {
          variantId: variantId,
          marketingRequested: wantsMarketing,
          marketingSucceeded: marketingSucceeded
        });
      } catch (error) {
        console.warn('Back in stock signup failed.', error);
        this.setStatus(this.getErrorMessage(error), 'error');
        this.emit('back-in-stock:failed', { variantId: variantId, status: error.status || 0 });
      } finally {
        this.setLoading(false);
      }
    }

    createBackInStockSubscription(companyId, variantId, email) {
      return this.sendRequest(BACK_IN_STOCK_ENDPOINT, companyId, {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email }
              }
            },
            channels: ['EMAIL']
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: '$shopify:::$default:::' + variantId
              }
            }
          }
        }
      });
    }

    async createShopifyMarketingSubscription(email) {
      var formData = new FormData(this.form);
      formData.delete('variant');
      formData.delete('marketing');
      formData.set('contact[email]', email);

      var body = new URLSearchParams();
      formData.forEach((value, key) => body.append(key, value));

      var response = await fetch(this.form.action, {
        method: 'POST',
        headers: {
          accept: 'text/html',
          'content-type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: body.toString(),
        credentials: 'same-origin',
        redirect: 'follow'
      });

      if (!response.ok) {
        var requestError = new Error('Shopify marketing signup failed with status ' + response.status);
        requestError.status = response.status;
        throw requestError;
      }

      var responseText = await response.text();
      var responseUrl = new URL(response.url || this.form.action, window.location.href);
      var responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      var returnedForm = this.form.id && responseDocument.getElementById(this.form.id);
      var isChallenge = responseUrl.pathname === '/challenge' || responseDocument.querySelector('form[action*="/challenge"]');
      var hasErrors = returnedForm && returnedForm.querySelector('[data-bis-shopify-errors], .errors');

      if (isChallenge || hasErrors) {
        var validationError = new Error('Shopify did not accept the marketing signup.');
        validationError.status = 400;
        throw validationError;
      }
    }

    async sendRequest(endpoint, companyId, payload) {
      var response = await fetch(endpoint + '?company_id=' + encodeURIComponent(companyId), {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) return;

      var error = new Error('Klaviyo request failed with status ' + response.status);
      error.status = response.status;
      try {
        var body = await response.json();
        error.detail = body && body.errors && body.errors[0] && body.errors[0].detail;
      } catch (parseError) {
        // Klaviyo may return an empty response body.
      }
      throw error;
    }

    getErrorMessage(error) {
      if (error.status === 400 || error.status === 422) {
        return 'Please check your email and selected size, then try again.';
      }
      if (error.status === 429) {
        return 'Too many requests were made. Please wait a moment and try again.';
      }
      return 'Something went wrong. Please try again.';
    }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: Object.assign({
          productId: String(this.dataset.productId || ''),
          variantId: String((this.variantInput && this.variantInput.value) || '')
        }, detail || {})
      }));
    }
  }

  if (!customElements.get('product-back-in-stock')) {
    customElements.define('product-back-in-stock', ProductBackInStock);
  }
})();
